import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

/**
 * Visual editor for exam question payloads. Emits JSON-serializable objects matching the backend.
 */
const ExamQuestionPayloadEditor = ({ questionType, payload, onChange }) => {
  const pl = payload && typeof payload === 'object' ? payload : {};

  const update = (patch) => {
    onChange({ ...pl, ...patch });
  };

  switch (questionType) {
    case 'true_false':
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Correct answer
          </Typography>
          <RadioGroup
            row
            value={pl.correct === true ? 'true' : 'false'}
            onChange={(e) => update({ correct: e.target.value === 'true' })}
          >
            <FormControlLabel value="true" control={<Radio />} label="True" />
            <FormControlLabel value="false" control={<Radio />} label="False" />
          </RadioGroup>
        </Box>
      );

    case 'yes_no':
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Correct answer
          </Typography>
          <RadioGroup
            row
            value={pl.correct === 'no' ? 'no' : 'yes'}
            onChange={(e) => update({ correct: e.target.value })}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </Box>
      );

    case 'single_choice': {
      const options = Array.isArray(pl.options) ? pl.options : [];
      const setOption = (idx, field, val) => {
        const next = options.map((o, i) => (i === idx ? { ...o, [field]: val } : o));
        update({ options: next, correct: pl.correct });
      };
      const addOption = () => {
        const keys = 'abcdefghijklmnop';
        const used = new Set(options.map((o) => o.key));
        let key = 'a';
        for (let i = 0; i < keys.length; i += 1) {
          if (!used.has(keys[i])) {
            key = keys[i];
            break;
          }
        }
        update({ options: [...options, { key, text: `Option ${options.length + 1}` }], correct: pl.correct || key });
      };
      const removeOption = (idx) => {
        const next = options.filter((_, i) => i !== idx);
        const removed = options[idx];
        let correct = pl.correct;
        if (correct === removed?.key && next[0]) correct = next[0].key;
        update({ options: next, correct });
      };
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Options — select the correct one
          </Typography>
          <Stack spacing={1}>
            {options.map((o, idx) => (
              <Stack key={o.key || idx} direction="row" spacing={1} alignItems="center">
                <Radio
                  checked={pl.correct === o.key}
                  onChange={() => update({ correct: o.key })}
                  value={o.key}
                />
                <TextField
                  size="small"
                  label="Key"
                  value={o.key}
                  onChange={(e) => setOption(idx, 'key', e.target.value)}
                  sx={{ width: 88 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Answer text"
                  value={o.text || ''}
                  onChange={(e) => setOption(idx, 'text', e.target.value)}
                />
                <IconButton size="small" onClick={() => removeOption(idx)} disabled={options.length < 2}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Button size="small" startIcon={<AddIcon />} onClick={addOption} sx={{ mt: 1 }}>
            Add option
          </Button>
        </Box>
      );
    }

    case 'multiple_choice': {
      const options = Array.isArray(pl.options) ? pl.options : [];
      const correctArr = Array.isArray(pl.correct) ? pl.correct : [];
      const toggle = (key) => {
        const set = new Set(correctArr);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        update({ correct: [...set] });
      };
      const setOption = (idx, field, val) => {
        const next = options.map((o, i) => (i === idx ? { ...o, [field]: val } : o));
        update({ options: next, correct: correctArr });
      };
      const addOption = () => {
        const keys = 'abcdefghijklmnop';
        const used = new Set(options.map((o) => o.key));
        let key = 'a';
        for (let i = 0; i < keys.length; i += 1) {
          if (!used.has(keys[i])) {
            key = keys[i];
            break;
          }
        }
        update({ options: [...options, { key, text: `Option ${options.length + 1}` }], correct: correctArr });
      };
      const removeOption = (idx) => {
        const next = options.filter((_, i) => i !== idx);
        const removed = options[idx];
        update({ options: next, correct: correctArr.filter((k) => k !== removed?.key) });
      };
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Options — check all correct answers
          </Typography>
          <Stack spacing={1}>
            {options.map((o, idx) => (
              <Stack key={o.key || idx} direction="row" spacing={1} alignItems="center">
                <Checkbox checked={correctArr.includes(o.key)} onChange={() => toggle(o.key)} />
                <TextField
                  size="small"
                  label="Key"
                  value={o.key}
                  onChange={(e) => setOption(idx, 'key', e.target.value)}
                  sx={{ width: 88 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Answer text"
                  value={o.text || ''}
                  onChange={(e) => setOption(idx, 'text', e.target.value)}
                />
                <IconButton size="small" onClick={() => removeOption(idx)} disabled={options.length < 2}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Button size="small" startIcon={<AddIcon />} onClick={addOption} sx={{ mt: 1 }}>
            Add option
          </Button>
        </Box>
      );
    }

    case 'short_text':
      return (
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Expected answer (optional)"
            value={pl.expected != null ? String(pl.expected) : ''}
            onChange={(e) => update({ expected: e.target.value })}
            helperText="Leave empty if answers are graded manually or many variants are accepted."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!pl.case_insensitive}
                onChange={(e) => update({ case_insensitive: e.target.checked })}
              />
            }
            label="Case-insensitive match"
          />
        </Stack>
      );

    case 'match': {
      const left = Array.isArray(pl.left) ? [...pl.left] : ['', ''];
      const right = Array.isArray(pl.right) ? [...pl.right] : ['', ''];
      const pairs = Array.isArray(pl.pairs) ? pl.pairs : [[0, 0], [1, 1]];

      const syncDims = (nextLeft, nextRight, prevPairs) => {
        const L = nextLeft.length;
        const R = nextRight.length;
        const nextPairs = [];
        for (let i = 0; i < Math.min(L, R); i += 1) {
          const old = prevPairs[i];
          const rIdx =
            old && typeof old[1] === 'number' && old[1] < R ? old[1] : Math.min(i, R - 1);
          nextPairs.push([i, rIdx]);
        }
        update({ left: nextLeft, right: nextRight, pairs: nextPairs });
      };

      const setLeft = (i, v) => {
        const next = [...left];
        next[i] = v;
        update({ left: next, right, pairs });
      };
      const setRight = (i, v) => {
        const next = [...right];
        next[i] = v;
        update({ left, right: next, pairs });
      };
      const addRow = () => {
        syncDims([...left, `Item ${left.length + 1}`], [...right, `Match ${right.length + 1}`], pairs);
      };
      const removeRow = (i) => {
        if (left.length <= 1) return;
        const nextL = left.filter((_, j) => j !== i);
        const nextR = right.filter((_, j) => j !== i);
        syncDims(nextL, nextR, pairs);
      };
      const setPairRight = (rowIdx, rightIdx) => {
        const nextPairs = pairs.map((p, j) => (j === rowIdx ? [p[0], parseInt(rightIdx, 10)] : p));
        update({ left, right, pairs: nextPairs });
      };

      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Match each left item to one on the right
          </Typography>
          <Stack spacing={2}>
            {left.map((l, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField
                    size="small"
                    label={`Left ${i + 1}`}
                    value={l}
                    onChange={(e) => setLeft(i, e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    →
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Matches right</InputLabel>
                    <Select
                      label="Matches right"
                      value={String((pairs[i] && pairs[i][1]) ?? 0)}
                      onChange={(e) => setPairRight(i, e.target.value)}
                    >
                      {right.map((_, ri) => (
                        <MenuItem key={ri} value={String(ri)}>
                          {right[ri] || `Right ${ri + 1}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={() => removeRow(i)} disabled={left.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Right column labels (editable)
            </Typography>
            {right.map((r, i) => (
              <TextField
                key={i}
                size="small"
                label={`Right ${i + 1}`}
                value={r}
                onChange={(e) => setRight(i, e.target.value)}
                sx={{ minWidth: 160 }}
              />
            ))}
          </Stack>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ mt: 1 }}>
            Add pair
          </Button>
        </Box>
      );
    }

    default:
      return <Typography color="text.secondary">Unsupported type for visual editor.</Typography>;
  }
};

export default ExamQuestionPayloadEditor;
